// The online test (docs/GAME_SERVERS_DESIGN.md, "Testing on your own
// computer"), as the desktop IDEs' Program > Run online test runs it: the
// program as a Shared World server room, and once its room is open, as a
// player who joins it. Here the server is the web runtime's server role
// (B3D_SHARED_SERVER) in a run frame of its own, off the page, until the
// server build (runtime-server.wasm) runs it in a Worker; main.js runs the
// player in the program pane, as Run runs a program. The server's frame
// shares no files with the page, so it posts its room's address
// (bbruntime/bbsharedworld.cpp), and the player is given the address itself
// as B3D_SHARED_TEST_ADDRESS. One browser runs one player: the desktop IDEs
// run up to four.

import { Runner } from "./runner.js";

// Shared World's own service, which the desktop IDEs use too.
export const SERVICE = "https://sw.blitz3d.plus";

// The networks, as the desktop IDEs offer them: half the ping each way, the
// jitter either side of it, and the share of messages lost each way
// (parseNetworkImpairment in modern/network).
export const NETWORKS = [
  { name: "none", label: "None: this computer's own speed", setting: "",
    effect: "Messages go as fast as this computer sends them." },
  { name: "good", label: "Good: 50 ms ping", setting: "ping=50,jitter=5",
    effect: "Each message takes 25 ms more each way, 5 ms more or less." },
  { name: "fair", label: "Fair: 100 ms ping, 1% lost", setting: "ping=100,jitter=15,loss=1",
    effect: "Each message takes 50 ms more each way, 15 ms more or less, and 1% are lost." },
  { name: "poor", label: "Poor: 200 ms ping, 3% lost", setting: "ping=200,jitter=30,loss=3",
    effect: "Each message takes 100 ms more each way, 30 ms more or less, and 3% are lost." },
  { name: "bad", label: "Bad: 300 ms ping, 10% lost", setting: "ping=300,jitter=50,loss=10",
    effect: "Each message takes 150 ms more each way, 50 ms more or less, and 10% are lost." },
];

// How full bots keep the room: -1 leaves it to the program, and 128 is more
// than any room holds, so bots fill whatever room the program makes.
export const BOTS = [
  { value: -1, label: "As the program sets them" },
  { value: 0, label: "None" },
  { value: 4, label: "Fill to 4 players" },
  { value: 16, label: "Fill to 16 players" },
  { value: 128, label: "Fill the room" },
];

// What the choices do, in a sentence for each.
export function describe({ bots = -1, network = "none" }) {
  const chosen = NETWORKS.find((entry) => entry.name === network) || NETWORKS[0];
  return "You play in the program pane, as Run plays the program." +
    (bots < 0 ? " The program chooses its bots." : bots === 0 ? " There are no bots." :
      bots >= 128 ? " Bots fill the room's empty places." : ` Bots keep ${bots} players in the room.`) +
    ` ${chosen.effect}`;
}

// How long the server has to open its room before the test says what it
// waits for, and gives up.
const WAITING_MS = 15000, GIVE_UP_MS = 120000;
// The server's last lines, which the console shows when it stops early.
const KEPT_LINES = 20;

export class OnlineTest {
  // runPath and runOriginFor are the run frames' (runner.js). service is the
  // Shared World service. events: { output(text, error), status(text),
  // roomOpened(address), failed() }: failed once the server stopped, or gave
  // up, before its room opened; the test is then over.
  constructor({ runPath, runOriginFor, service = SERVICE, bots = -1, network = "none", events }) {
    this.runPath = runPath;
    this.runOriginFor = runOriginFor;
    this.service = service;
    this.bots = bots;
    this.network = NETWORKS.some((entry) => entry.name === network) ? network : "none";
    this.events = events;
    this.address = null;
    this.ended = false;
    this.lines = [];
  }

  // The environment of the server (player 0) or the player (1).
  environment(player) {
    const environment = { B3D_PLAY_ONLINE: "1", B3D_SHARED_SERVICE: this.service };
    if (player) return { ...environment, B3D_SHARED_TEST_ADDRESS: this.address };
    // The server writes its room's address to this file, in its own frame,
    // and posts it to the page.
    environment.B3D_SHARED_SERVER = "1";
    environment.B3D_SHARED_TEST_ROOM = "/tmp/b3d-online-test-room.txt";
    if (this.bots >= 0) environment.B3D_SHARED_TEST_BOTS = String(this.bots);
    const setting = NETWORKS.find((entry) => entry.name === this.network).setting;
    if (setting) environment.B3D_SHARED_TEST_NETWORK = setting;
    return environment;
  }

  // Starts the server: { title, wasm, assets, launchDirectory, commandLine }.
  start({ title, wasm, assets, launchDirectory = ".", commandLine = "" }) {
    const pane = this.pane = document.createElement("div");
    pane.className = "online-test-server";
    pane.setAttribute("aria-hidden", "true");
    pane.style.cssText = "position: fixed; left: -10000px; top: 0; width: 640px; height: 400px;";
    document.body.append(pane);
    const keep = (text, error) => {
      this.lines.push({ text, error });
      if (this.lines.length > KEPT_LINES) this.lines.shift();
    };
    this.server = new Runner({ pane, runPath: this.runPath, runOriginFor: this.runOriginFor, pinned: true, events: {
      started() {}, firstFrame() {}, responding() {}, debug() {}, file() {}, close() {}, float() {},
      output: (lines) => {
        for (const line of lines) {
          keep(line.text, line.error);
          if (!this.ended) this.events.output(`Server: ${line.text}`, line.error);
        }
      },
      pageMessage: (message) => {
        if (this.ended || this.address || !message || message.protocol !== "blitz3d.online-test" ||
            message.type !== "room" || typeof message.address !== "string" || !message.address) return;
        this.address = message.address;
        this.events.roomOpened(this.address);
      },
      ended: (code, message) => {
        if (this.ended) return;
        if (message && !this.lines.some((line) => line.text === message)) this.events.output(`Server: ${message}`, true);
        if (!this.address) {
          this.events.status("The online test's server stopped before its room opened: the console shows why.");
          this.fail();
        } else {
          this.events.status("The online test's server stopped: the console shows why.");
        }
      },
    } });
    this.server.run({ title: `${title} (server)`, debug: false, wasm, assets, launchDirectory, commandLine,
      focus: false, openUrls: false, environment: this.environment(0) });
    this.started = performance.now();
    this.timer = setInterval(() => this.poll(), 1000);
  }

  // Says what the server waits for, and gives up after two minutes.
  poll() {
    if (this.ended || this.address) return clearInterval(this.timer);
    const waited = performance.now() - this.started;
    const needs = "the program must start Shared World (StartSharedWorld), or play a world whose Online section is on.";
    if (waited > GIVE_UP_MS) {
      this.events.status(`The online test's server did not open a room in two minutes: ${needs}`);
      this.fail();
    } else if (waited > WAITING_MS && !this.waitingSaid) {
      this.waitingSaid = true;
      this.events.status(`Waiting for the online test's server to open its room: ${needs}`);
    }
  }

  fail() {
    this.end();
    this.events.failed();
  }

  // Ends the server. Returns whether it was running.
  end() {
    if (this.ended) return false;
    this.ended = true;
    clearInterval(this.timer);
    if (this.server) this.server.stop();
    if (this.pane) this.pane.remove();
    return true;
  }
}
